/**
 * Unit conversion constants
 */
export const UNITS = {
  IMPERIAL: 'imperial',
  METRIC: 'metric'
} as const;

export type UnitSystem = typeof UNITS[keyof typeof UNITS];

export interface Calculation {
  id: string;
  width: number;
  length: number;
  thickness: number;
  tonnage: number;
  timestamp: number;
  unit: UnitSystem;
}

/**
 * Calculates the asphalt tonnage required based on dimensions
 */
export const calculateAsphaltTonnage = (
  width: number,
  length: number,
  thickness: number,
  unit: UnitSystem = UNITS.IMPERIAL
): number => {
  if (!width || !length || !thickness || 
      width <= 0 || length <= 0 || thickness <= 0) {
    return 0;
  }

  // Convert metric to imperial if needed
  const widthFt = unit === UNITS.METRIC ? width * 3.28084 : width;
  const lengthFt = unit === UNITS.METRIC ? length * 3.28084 : length;
  const thicknessIn = unit === UNITS.METRIC ? thickness * 0.393701 : thickness;

  // Convert area from square feet to square yards (9 sq ft = 1 sq yd)
  const areaInSquareYards = (widthFt * lengthFt) / 9;
  
  // Calculate weight in pounds
  const weightInPounds = areaInSquareYards * thicknessIn * 110;
  
  // Convert pounds to tons (no rounding)
  return weightInPounds / 2000;
};

/**
 * Validates if a string is a valid positive number
 */
export const isValidPositiveNumber = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

/**
 * Formats a number to a string with specified decimal places
 */
export const formatNumber = (num: number, decimals: number = 2): string => {
  return num.toFixed(decimals);
};

/**
 * Gets the dimension unit label based on the unit system
 */
export const getDimensionUnit = (unit: UnitSystem): string => {
  return unit === UNITS.METRIC ? 'meters' : 'feet';
};

/**
 * Gets the thickness unit label based on the unit system
 */
export const getThicknessUnit = (unit: UnitSystem): string => {
  return unit === UNITS.METRIC ? 'centimeters' : 'inches';
};